import { type GwpOffer } from "@tablescape/state";

export type GwpInteractionAction = "removed" | "dismissed";

export type GwpInteractionRecord = {
  action: GwpInteractionAction;
  hasInteracted: true;
  /** Per gift variant, when one offer auto-adds more than one product. */
  optedOutByMerchandiseId?: Record<string, number>;
  /** Gift units the shopper removed. Units added after that still auto-add. */
  optedOutQuantity?: number;
};

const STORAGE_KEY = "gwp-interaction";

const listeners = new Set<() => void>();
let cachedRaw: string | null | undefined;
let cachedRecords: Record<string, GwpInteractionRecord> = {};
// Set when sessionStorage rejects a write. Reads then stay on the in-memory
// snapshot, because the stored string is now stale.
let storageWriteBlocked = false;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

type GwpInteractionOffer =
  | Pick<Extract<GwpOffer, { kind: "product" }>, "kind" | "trigger">
  | Pick<Extract<GwpOffer, { kind: "spending" }>, "kind" | "trigger">;

export const gwpInteractionKeyForOffer = (
  offer: GwpInteractionOffer
): string => {
  switch (offer.kind) {
    case "spending":
      return `spending:${offer.trigger.spendingThreshold}`;
    case "product":
      return `product:${offer.trigger.parentVariantId}`;
    default: {
      const _exhaustive: never = offer;
      return _exhaustive;
    }
  }
};

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === "number" && value > 0;

const positiveCounts = (value: unknown): Record<string, number> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const counts: Record<string, number> = {};
  for (const [merchandiseId, count] of Object.entries(value)) {
    if (isPositiveNumber(count)) {
      counts[merchandiseId] = count;
    }
  }

  return Object.keys(counts).length > 0 ? counts : undefined;
};

const buildRecord = (
  action: GwpInteractionAction,
  optedOutQuantity: unknown,
  optedOutByMerchandiseId?: unknown
): GwpInteractionRecord => {
  const byMerchandise = positiveCounts(optedOutByMerchandiseId);
  // The per-gift map is the source of truth when present; keep the total in
  // sync so single-gift readers keep working.
  const quantity = byMerchandise
    ? Object.values(byMerchandise).reduce((total, count) => total + count, 0)
    : optedOutQuantity;

  return {
    action,
    hasInteracted: true,
    ...(isPositiveNumber(quantity) ? { optedOutQuantity: quantity } : {}),
    ...(byMerchandise ? { optedOutByMerchandiseId: byMerchandise } : {}),
  };
};

const parseRecords = (
  raw: string | null
): Record<string, GwpInteractionRecord> => {
  if (!raw) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).flatMap(([key, value]) =>
        isRecord(value) &&
        value.hasInteracted === true &&
        (value.action === "removed" || value.action === "dismissed")
          ? [
              [
                key,
                buildRecord(
                  value.action,
                  value.optedOutQuantity,
                  value.optedOutByMerchandiseId
                ),
              ],
            ]
          : []
      )
    );
  } catch {
    return {};
  }
};

export const readGwpInteractions = (): Record<string, GwpInteractionRecord> => {
  if (typeof window === "undefined" || storageWriteBlocked) {
    return cachedRecords;
  }

  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return cachedRecords;
  }
  if (raw === cachedRaw) {
    return cachedRecords;
  }

  cachedRaw = raw;
  cachedRecords = parseRecords(raw);
  return cachedRecords;
};

const writeAll = (records: Record<string, GwpInteractionRecord>) => {
  if (typeof window === "undefined") {
    return;
  }

  cachedRecords = records;

  try {
    const raw = JSON.stringify(records);
    window.sessionStorage.setItem(STORAGE_KEY, raw);
    cachedRaw = raw;
    storageWriteBlocked = false;
  } catch {
    storageWriteBlocked = true;
  }

  listeners.forEach((listener) => listener());
};

export const readGwpInteraction = (key: string): GwpInteractionRecord | null =>
  readGwpInteractions()[key] ?? null;

export const writeGwpInteraction = (
  key: string,
  action: GwpInteractionAction,
  optedOutQuantity?: number,
  optedOutByMerchandiseId?: Record<string, number>
) => {
  writeAll({
    ...readGwpInteractions(),
    [key]: buildRecord(action, optedOutQuantity, optedOutByMerchandiseId),
  });
};

export const clearInactiveGwpInteractions = (
  activeKeys: ReadonlySet<string>
) => {
  for (const key of Object.keys(readGwpInteractions())) {
    if (!activeKeys.has(key)) {
      clearGwpInteraction(key);
    }
  }
};

export const clearGwpInteraction = (key: string) => {
  const { [key]: removed, ...records } = readGwpInteractions();
  if (!removed) {
    return;
  }

  writeAll(records);
};

export const subscribeGwpInteraction = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};
